/**
 * Change a lead's stage.
 *
 * Wraps PATCH /v1/leads/{id}/stage?to=<LeadStage>. Two backend rules are
 * enforced here so the user never eats a 400:
 *   1. LOST requires an active lostReasonCode  → we show the taxonomy picker.
 *   2. DELIVERED / LOST are terminal            → we refuse to open on a closed lead.
 */
import { useEffect, useRef, useState } from 'react';
import { LEAD_STAGES, STAGE_META, isClosedStage } from '../lib/crm';
import { pipelineService } from '../lib/services';
import { apiErrorMessage } from '../lib/apiError';

export default function StageChangeModal({ open, lead, initialTarget, onClose, onChange }) {
  const current = lead?.stage;

  const [target, setTarget] = useState(current);
  const [reasons, setReasons] = useState([]);
  const [lostReasonCode, setLostReasonCode] = useState('');
  const [lostNotes, setLostNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Latest props, readable from effects without being dependencies. Callers
  // pass `onClose` as an inline arrow (new identity every render), and the
  // lead page re-renders every few seconds from its polling — when `onClose`
  // was a dependency here, each poll re-ran the reset below and snapped the
  // user's choice back to the current stage.
  const latest = useRef({ current, initialTarget, onClose });
  latest.current = { current, initialTarget, onClose };

  // Reset the form ONLY when the dialog opens — never on a re-render.
  useEffect(() => {
    if (!open) return;
    const { current: cur, initialTarget: init } = latest.current;
    // When opened from a kanban drop we already know the destination
    // (e.g. LOST) — pre-select it so the user only supplies the reason.
    setTarget(init && init !== cur ? init : cur);
    setLostReasonCode('');
    setLostNotes('');
    setBusy(false);
    setError(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') latest.current.onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Lost-reason taxonomy — only the active codes are accepted by the backend.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    pipelineService.lostReasons()
      .then((rows) => {
        if (cancelled) return;
        const list = Array.isArray(rows) ? rows : [];
        setReasons(list);
        if (list.length) setLostReasonCode((c) => c || list[0].code);
      })
      .catch(() => { /* picker degrades to free-text notes only */ });
    return () => { cancelled = true; };
  }, [open]);

  if (!open || !lead) return null;

  const closed      = isClosedStage(current);
  const needsReason = target === 'LOST';
  const unchanged   = target === current;
  const canSubmit   = !busy && !closed && !unchanged
                      && (!needsReason || !!lostReasonCode);

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      await onChange(target, needsReason
        ? { lostReasonCode, lostNotes: lostNotes.trim() || undefined }
        : {});
      onClose?.();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not change the stage'));
      setBusy(false);
    }
  };

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="scm-title"
         onClick={onClose} className="modal-backdrop">
      <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}
            style={{ width: 'min(480px, 92vw)' }}>
        <div className="modal-header">
          <div>
            <div id="scm-title" style={{ fontSize: 16, fontWeight: 600 }}>Change stage</div>
            <div className="mono text-xs text-muted" style={{ marginTop: 2 }}>
              Currently {STAGE_META[current]?.label || current}
            </div>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {closed ? (
            <div className="text-xs" style={{ color: 'var(--warning)', lineHeight: 1.6 }}>
              This lead is closed ({STAGE_META[current]?.label || current}). Closed leads can't
              be moved directly — reopen it first to put it back in the pipeline.
            </div>
          ) : (
            <>
              <div className="form-field">
                <label htmlFor="scm-stage">Move to</label>
                <select
                  id="scm-stage"
                  value={target}
                  onChange={(e) => setTarget(e.target.value)}
                  autoFocus
                >
                  {LEAD_STAGES.map(s => (
                    <option key={s} value={s} disabled={s === current}>
                      {STAGE_META[s]?.label || s}{s === current ? ' — current' : ''}
                    </option>
                  ))}
                </select>
              </div>

              {needsReason && (
                <>
                  <div className="form-field">
                    <label htmlFor="scm-reason">Lost reason <span style={{ color: 'var(--danger)' }}>*</span></label>
                    {reasons.length ? (
                      <select
                        id="scm-reason"
                        value={lostReasonCode}
                        onChange={(e) => setLostReasonCode(e.target.value)}
                      >
                        {reasons.map(r => (
                          <option key={r.code} value={r.code}>{r.label || r.code}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        id="scm-reason"
                        value={lostReasonCode}
                        onChange={(e) => setLostReasonCode(e.target.value.toUpperCase())}
                        placeholder="e.g. LOST_TO_COMPETITOR"
                      />
                    )}
                  </div>
                  <div className="form-field">
                    <label htmlFor="scm-notes">Notes</label>
                    <textarea
                      id="scm-notes"
                      rows={3}
                      value={lostNotes}
                      onChange={(e) => setLostNotes(e.target.value)}
                      placeholder="What happened? (optional)"
                    />
                  </div>
                </>
              )}

              {target === 'DELIVERED' && (
                <div className="text-xs text-muted" style={{ lineHeight: 1.6 }}>
                  Delivered is terminal — the lead closes and leaves the active pipeline.
                </div>
              )}
            </>
          )}

          {error && <div className="text-xs" style={{ color: 'var(--danger)' }}>{error}</div>}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn small" onClick={onClose} disabled={busy}>Cancel</button>
          <button
            type="submit"
            className={`btn small ${target === 'LOST' ? 'danger' : 'primary'}`}
            disabled={!canSubmit}
          >
            {busy ? 'Saving…' : `Move to ${STAGE_META[target]?.label || target}`}
          </button>
        </div>
      </form>
    </div>
  );
}
